import { createContext, useContext, useState, type ReactNode } from "react";

type Unit = "C" | "F" | "CF";

interface UnitContextValue {
  unit: Unit;
  setUnit: (u: Unit) => void;
  display: (tempC: number) => string;
}

const UnitContext = createContext<UnitContextValue>({
  unit: "C",
  setUnit: () => {},
  display: (t) => `${Math.round(t)}°C`,
});

function toF(c: number) { return Math.round(c * 9 / 5 + 32); }

export function UnitProvider({ children }: { children: ReactNode }) {
  const [unit, setUnit] = useState<Unit>(() => {
    if (typeof window !== "undefined") {
      return (localStorage.getItem("vertali_unit") as Unit) || "CF";
    }
    return "CF";
  });

  const handleSet = (u: Unit) => {
    setUnit(u);
    localStorage.setItem("vertali_unit", u);
  };

  const display = (tempC: number) => {
    const c = Math.round(tempC);
    const f = toF(tempC);
    if (unit === "F") return `${f}°F`;
    if (unit === "CF") return `${c}°C / ${f}°F`;
    return `${c}°C`;
  };

  return (
    <UnitContext.Provider value={{ unit, setUnit: handleSet, display }}>
      {children}
    </UnitContext.Provider>
  );
}

export function useUnit() {
  return useContext(UnitContext);
}
